import { faRoute } from '@fortawesome/pro-solid-svg-icons';
import {
  StrategyConfig,
  StrategyValidationContext,
  CreateStrategyPayload,
  UpdateStrategyPayload,
} from '../types';
import {
  validatePatientJourneyCriteria,
  hasUnfilledCriteriaPlaceholders,
} from '../../Admin/utils/strategyValidation';
import PatientJourneyConfiguration from '../../Admin/AddEditStrategy/components/StrategyTypes/PatientJourneyConfiguration';
import { transformCriteriaInputs } from './transformers';

/**
 * Configuration for Patient Journey strategy type
 *
 * Engages/expands providers based on patient journey and care pathway analysis.
 * Uses separate index event and target event criteria.
 */
export const patientJourneyConfig: StrategyConfig = {
  type: 'PATIENT_JOURNEY',
  label: 'Patient Journey',
  description:
    'Engage/Expand providers based on patient journey and care pathway analysis',
  icon: faRoute,
  mode: 'engage',
  disabled: false,

  ConfigComponent: PatientJourneyConfiguration,

  requiredFields: [
    'label',
    'description',
    'index_event_template',
    'target_event_template',
  ],

  validateProgress: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) {
      return !!(
        context.strategyDetails?.criteria_template ||
        context.strategyDetails?.codeSetId ||
        context.strategyDetails?.mode
      );
    }
    // Patient Journey needs both index and target event criteria configured
    // Also check that all placeholders have valid data
    const indexTemplate = context.indexEventCriteria?.criteria_template;
    const targetTemplate = context.targetEventCriteria?.criteria_template;

    if (!indexTemplate || !targetTemplate) {
      return false;
    }

    // If either criteria has unfilled placeholders, progress is not complete
    if (
      hasUnfilledCriteriaPlaceholders(context.indexEventCriteria) ||
      hasUnfilledCriteriaPlaceholders(context.targetEventCriteria)
    ) {
      return false;
    }

    return true;
  },

  validateComplete: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) return true;
    return validatePatientJourneyCriteria(
      context.indexEventCriteria,
      context.targetEventCriteria
    );
  },

  serializeForCreate: (
    context: StrategyValidationContext
  ): CreateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      type: 'PATIENT_JOURNEY',
      max_opportunities: context.formData.max_opportunities,
      index_event_template:
        context.indexEventCriteria?.criteria_template || '',
      index_event_inputs: transformCriteriaInputs(
        context.indexEventCriteria?.criteria_inputs
      ),
      target_event_template:
        context.targetEventCriteria?.criteria_template || '',
      target_event_inputs: transformCriteriaInputs(
        context.targetEventCriteria?.criteria_inputs
      ),
      target_event_interval:
        Math.max(1, Number(context.patientJourneyData?.target_event_interval) || 6),
      target_event_direction: context.patientJourneyDirection ?? 'AFTER',
      index_event_radius: context.patientJourneyData?.radius ?? 0,
    };
  },

  serializeForUpdate: (
    context: StrategyValidationContext
  ): UpdateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      max_opportunities: context.formData.max_opportunities,
      index_event_template:
        context.indexEventCriteria?.criteria_template || '',
      index_event_inputs: transformCriteriaInputs(
        context.indexEventCriteria?.criteria_inputs
      ),
      target_event_template:
        context.targetEventCriteria?.criteria_template || '',
      target_event_inputs: transformCriteriaInputs(
        context.targetEventCriteria?.criteria_inputs
      ),
      target_event_interval:
        Math.max(1, Number(context.patientJourneyData?.target_event_interval) || 6),
      target_event_direction: context.patientJourneyDirection ?? 'AFTER',
      index_event_radius: context.patientJourneyData?.radius ?? 0,
    };
  },

  features: {
    hasMaxOpportunities: true,
    usesPatientJourneyEvents: true,
    usesRadius: true,
  },
};
